import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Branch } from 'models';
import { getItemAsJson, setItemAsJson } from 'helpers';

import { UserService } from 'services/user.service';

import { FcpModalService } from 'fc-ui-components';
import { SelectableBranch } from './types';
import { distinctUntilChanged, filter, shareReplay, startWith } from 'rxjs/operators';
import { tag } from 'rxjs-spy/operators';
import { LogTag } from 'app/b2b-order/services/observable-spy.service';
import { StoreService } from '../store.service';

@Injectable({ providedIn: 'root' })
export class StoreSelectorService {
    private _selectedStores: BehaviorSubject<SelectableBranch[]> = new BehaviorSubject<SelectableBranch[]>([]);
    public readonly selectedStores: Observable<SelectableBranch[]>;

    private _selectedStore: BehaviorSubject<Branch> = new BehaviorSubject<Branch>(null);
    public selectedStore: Observable<Branch>;

    private selectedStoresStorageKey = 'selectedStores';
    private selectedStoreKey = 'selectedStoreShoppingCart';
    public subscriptions = new Subscription();
    constructor(
        private userService: UserService,
        private storeService: StoreService,
        private fcpModalService: FcpModalService
    ) {
        this.fetchStores();
        this.selectedStores = this._selectedStores.pipe(
            startWith([]),
            distinctUntilChanged(),
            shareReplay(),
            tag(LogTag.reorderSelectedBrand)
        );
        this.selectedStore = this._selectedStore.pipe(startWith(null));
    }

    public displayStoresModal(chooseStoreModalId: string) {
        const modalConfig = { chooseStoresModalId: chooseStoreModalId, disableClose: false };
        if (!this._selectedStores.value.length) {
            modalConfig.disableClose = true;
        }

        try {
            this.fcpModalService.open(chooseStoreModalId, modalConfig);
        } catch (error) {
            console.log(error);
        }
        return this.fcpModalService.onClosed(chooseStoreModalId);
    }

    // Use for single selection
    public updateSelectedStore(store: Branch) {
        this._selectedStore.next(store);
    }

    public upsertSelectedStores(storeToAdd: Partial<SelectableBranch>) {
        const selectedStores = this._selectedStores.getValue();
        const { _id: storeId } = storeToAdd;
        const storeIndex = selectedStores.findIndex((store) => store._id === storeId);
        const storeToUpdate: SelectableBranch =
            selectedStores[storeIndex] || this.storeService.stores.getValue().find((store) => store._id === storeId);
        if (_.isEmpty(storeToUpdate)) {
            console.error('No access to store');
            return;
        }

        if (storeIndex === -1) {
            selectedStores.push(storeToUpdate);
            this.updateSelectedStores(selectedStores);
        }

        return storeToUpdate;
    }

    public updateSelectedStores(stores: SelectableBranch[]) {
        this._selectedStores.next(stores.map((store) => store));

        setItemAsJson(
            this.selectedStoresStorageKey,
            stores.map((store) => store._id)
        );
    }

    public fetchStores() {
        this._selectedStores.next([]);
        if (this.userService.isRetailer()) {
            this.subscriptions.add(
                this.storeService.stores.pipe(filter((stores) => stores.length > 0)).subscribe((stores) => {
                    const ids = getItemAsJson(this.selectedStoresStorageKey) || [];
                    const selectedStores = ids
                        .map((id: string) => _.find(stores, (store) => store._id === id))
                        .filter(Boolean);
                    const combinedStores = _.unionBy([...this._selectedStores.value, ...selectedStores], '_id');
                    this._selectedStores.next(combinedStores.map((store) => store));
                })
            );
        }
    }

    public clearSelectedStores() {
        this._selectedStores.next([]);
        this._selectedStores.next([]);
        setItemAsJson(this.selectedStoresStorageKey, []);
        setItemAsJson(this.selectedStoreKey, null);
    }
}
